import { LoadingButton } from '@mui/lab';
import { Stack, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { LinkAuthenticationElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Config } from 'src/config';
import { CommonHelper } from 'src/utils/commonHelper';
import { paymentModuleUrl } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';

export default function CheckoutForm({ amount, currencySymbol,planName }) {
  const { t: translate } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToaster();
  const [saveForAutopay, setSaveForAutopay] = useState(false);

  const getUserEmail = () => {
    let email = '';
    let userDetails = CommonHelper.GetLoggedInUserDetailsFromLocalStorage();
    if (!userDetails) userDetails = CommonHelper.GetBrandInfoFromLocalStorage('user_details');
    if (userDetails) email = userDetails.email;
    return email;
  };
  const userEmail = getUserEmail();
useEffect(() => {
    if (planName && amount) {
      localStorage.setItem('planName', planName);
      localStorage.setItem('planValue', amount.toString());
      localStorage.setItem('currency', currencySymbol === "₹" ? "INR" : "USD")
    }
  }, [planName, amount]);
  useEffect(() => {
    var emailElement = document.getElementById('Field-emailInput');
    if (emailElement) emailElement.disabled = true;
  }, []);

  useEffect(() => {
    if (!stripe) {
      return;
    }
    const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');
    if (!clientSecret) {
      return;
    }
    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          showToast(`${translate('billing.subscription_info.payment_success')}!`, 'e');
          break;
        case 'processing':
          showToast(translate('billing.subscription_info.payment_processing'), 'e');
          break;
        case 'requires_payment_method':
          showToast(translate('billing.subscription_info.payment_not_success'), 'e');
          break;
        default:
          showToast(translate('common.something_went_wrong'), 'e');
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: Config.Stripe.redirectUri,
      },
    });
    showToast(error.message ? error.message : translate('common.something_went_wrong'), 'e');

    setIsLoading(false);
  };

  const handleSaveForAutopay = async () => {
    // 1) ask backend for SetupIntent client_secret
    setIsLoading(true);
    try {
      // org id lives in the initial-load object stored under LocalStorage 'initial-load'
      // Prefer org id from initial-load (brand info). Fallback to user_details.org_id if necessary.
      let orgId = CommonHelper.GetBrandInfoFromLocalStorage('org_id');
      if (!orgId) {
        const user = CommonHelper.GetLoggedInUserDetailsFromLocalStorage();
        orgId = user && user.org_id ? user.org_id : null;
      }
      if (!orgId) {
        showToast('Org not found. Please select an organization/brand and try again.', 'e');
        setIsLoading(false);
        return;
      }
      const res = await fetch(paymentModuleUrl.createSetupIntent, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId }),
      });
      if (!res.ok) throw new Error('Failed to create setup intent');
      const payload = await res.json();
      // Backend may wrap data under data or return directly. Try common shapes.
      const clientSecret = (payload && payload.data && payload.data.client_secret) || payload.client_secret || (payload && payload.data && payload.data.client_secret);
      if (!clientSecret) {
        showToast('Could not get setup intent', 'e');
        setIsLoading(false);
        return;
      }
      // 2) confirm setup with Stripe using the client secret returned by the backend
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {},
      }, {clientSecret});
      if (setupError) {
        showToast(setupError.message || 'Error saving card', 'e');
        setIsLoading(false);
        return;
      }
      const paymentMethod = setupIntent && setupIntent.payment_method ? setupIntent.payment_method : null;
      if (!paymentMethod) {
        showToast('No payment method saved', 'e');
        setIsLoading(false);
        return;
      }
      // 3) call backend to save default payment method and enable autopay
      const saveRes = await fetch(paymentModuleUrl.savePaymentMethod, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId, payment_method: paymentMethod, enable_autopay: true }),
      });
      if (!saveRes.ok) {
        showToast('Failed to save payment method', 'e');
        setIsLoading(false);
        return;
      }
      showToast(translate('billing.subscription_info.autopay_enabled'), 's');
    } catch (err) {
      showToast(err.message || 'Error enabling autopay', 'e');
    }
    setIsLoading(false);
  };

  return (
    <>
      <form id="payment-form" onSubmit={handleSubmit}>
        <LinkAuthenticationElement
          id="link-authentication-element"
          options={{
            defaultValues: {
              email: userEmail,
            },
          }}
        />
        <PaymentElement id="payment-element" />
        <FormControlLabel
          control={<Checkbox checked={saveForAutopay} onChange={(e) => setSaveForAutopay(e.target.checked)} />}
          label={translate('billing.subscription_info.save_for_autopay')}
        />
        <Stack direction="row" alignItems="center" sx={{ mt: 3, mb: 1, float: 'left' }}>
          <Typography sx={{ fontWeight: 800 }}>
            {translate('common.total')} = {currencySymbol}
            {amount}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ mt: 2, mb: 1, float: 'right' }}>
          <LoadingButton
            disabled={isLoading || !stripe || !elements}
            id="submit"
            type="submit"
            loading={isLoading}
            color="primary"
            variant="outlined"
          >
            {translate('billing.subscription_info.pay_now')}
            Pay Now
          </LoadingButton>
        </Stack>
        {saveForAutopay && (
          <Stack direction="row" alignItems="center" sx={{ mt: 2, mb: 1, float: 'right' }}>
            <LoadingButton
              disabled={isLoading || !stripe || !elements}
              onClick={handleSaveForAutopay}
              loading={isLoading}
              color="primary"
              variant="contained"
            >
              {translate('billing.subscription_info.save_and_enable_autopay')}
            </LoadingButton>
          </Stack>
        )}
      </form>
    </>
  );
}
